import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicApiClient } from '../api/apiClient';
import { useAuthStore } from '../store/authStore';
import { isLimitedToOwnCompany } from '../utils/roleUtils';
import { useMyCompany } from '../hooks/useMyCompany';
import toast from 'react-hot-toast';
import { ListAccountsRole } from '../../generated-src/client/account/api';
import { UpdateAccountRequest } from '../../generated-src/client/account/api';
import { apiService } from '../services/apiService';
import { avatarColorCache } from '../utils/avatarColorCache';
import { RoleSelectWithLabel, CompanySelectWithLabel, SelectWithLabel } from './ui/universal-select';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { loadCompaniesForSelect } from '../services/companySelectOptionsService';

// Локальный тип User
interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: ListAccountsRole;
  teamId?: number;
  companyId?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Company {
  id: number;
  name: string;
  description?: string | null;
  status: 'ACTIVE' | 'PILOT' | 'SUSPENDED' | 'INACTIVE';
}

interface UserEditFormData {
  email: string;
  firstName: string;
  lastName: string;
  role: ListAccountsRole;
  password: string;
  confirmPassword: string;
  companyId?: number;
  isActive: boolean;
}

// Настройки формы в зависимости от режима
interface FormConfig {
  title: string;
  showRoleField: boolean;
  showPasswordFields: boolean;
  showLogoutButton: boolean;
  submitButtonText: string;
  cancelButtonText: string;
  isPersonalInfo: boolean;
}

// Улучшенный компонент FigmaTextInput с поддержкой ошибок валидации
const FigmaTextInput = ({
  label,
  type = "text",
  value,
  onChange,
  width = "w-full",
  required = false,
  error = null,
  placeholder,
  disableAutofill = false
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  width?: string;
  required?: boolean;
  error?: string | null;
  placeholder?: string;
  disableAutofill?: boolean;
}) => (
  <div className={`box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 ${width}`}>
    <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
      <p className="block leading-[20px]">{label} {required && '*'}</p>
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={`bg-[#ffffff] relative rounded-xl shrink-0 w-full py-[10px] px-[24px] border focus:outline-none h-[44px] text-[14px] font-['Inter:Regular',_sans-serif] text-[#525866] placeholder:text-[#868c98] ${
        error ? 'border-red-500' : 'border-[#e2e4e9]'
      }`}
      onFocus={(e) => {
        const inputEl = e.currentTarget as HTMLInputElement;
        inputEl.style.borderColor = error ? '#ef4444' : 'var(--brand-primary, #e16349)';
        if (label.toLowerCase().includes('пароль') || (disableAutofill && type === 'password')) {
          inputEl.removeAttribute('readonly');
        }
      }}
      onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = error ? '#ef4444' : '#e2e4e9'; }}
      placeholder={placeholder || `Введите ${label.toLowerCase()}`}
      autoComplete={label.toLowerCase().includes('пароль') ? "new-password" : (disableAutofill ? "new-password" : undefined)}
      data-form-type={label.toLowerCase().includes('пароль') ? "password-field" : (disableAutofill ? "personal-edit" : "other")}
      data-lpignore={label.toLowerCase().includes('пароль') ? "true" : (disableAutofill ? "true" : undefined)}
      data-1p-ignore={label.toLowerCase().includes('пароль') ? "true" : (disableAutofill ? "true" : undefined)}
      name={label.toLowerCase().includes('пароль') ? `fake-password-${Date.now()}` : (disableAutofill ? `fake-${type}-${Date.now()}` : undefined)}
      id={label.toLowerCase().includes('пароль') ? `fake-password-${Date.now()}` : (disableAutofill ? `fake-${type}-${Date.now()}` : undefined)}
      readOnly={label.toLowerCase().includes('пароль') ? true : (disableAutofill && type === "password" ? true : false)}
      
    />
    {error && (
      <div className="text-red-500 text-[12px] font-['Inter:Regular',_sans-serif] mt-1">
        {error}
      </div>
    )}
  </div>
);

const getRoleLevel = (role: string): number => {
  const normalizedRole = String(role).toUpperCase();
  switch (normalizedRole) {
    case 'SUPER_ADMIN':
      return 4;
    case 'OWNER':
      return 3;
    case 'ADMIN':
      return 2;
    case 'RECRUITER':
      return 1;
    default:
      return 0;
  }
};

const canChangeRole = (
  currentUserRole: string, 
  targetUserRole: string | undefined, 
  isEditingSelf: boolean
): boolean => {
  if (isEditingSelf) return false;
  if (!targetUserRole) return true;
  
  const currentLevel = getRoleLevel(currentUserRole);
  const targetLevel = getRoleLevel(targetUserRole);
  
  return targetLevel < currentLevel;
};

const RoleSelectComponent = ({
  value,
  onChange,
  width = "w-full",
  targetUserRole,
  isEditingSelf = false,
  hasCompany = false,
  companyHasOwner = false
}: {
  value: ListAccountsRole;
  onChange: (value: ListAccountsRole) => void;
  width?: string;
  targetUserRole?: string;
  isEditingSelf?: boolean;
  hasCompany?: boolean;
  companyHasOwner?: boolean;
}) => (
  <div className={width}>
    <RoleSelectWithLabel
      label="Роль *"
      value={value}
      onChange={(val) => onChange(val as ListAccountsRole)}
      targetUserRole={targetUserRole}
      isEditingSelf={isEditingSelf}
      hasCompany={hasCompany}
      companyHasOwner={companyHasOwner}
    />
  </div>
);

// Компонент для выбора компании
const CompanySelect = ({
  value,
  onChange,
  companies,
  width = "w-full",
  currentUserRole,
  currentUserCompanyId,
  disabled = false,
  myCompany
}: {
  value?: number;
  onChange: (value: number | undefined) => void;
  companies: Company[];
  width?: string;
  currentUserRole?: string;
  currentUserCompanyId?: number;
  disabled?: boolean;
  myCompany?: any;
}) => {
  const role = String(currentUserRole).toUpperCase();
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isAdminOrOwner = role === 'ADMIN' || role === 'OWNER';
  
  // Фильтруем компании в зависимости от роли
  const getFilteredCompanies = () => {
    if (isSuperAdmin) {
      return companies;
    } else if (isAdminOrOwner) {
      if (myCompany) {
        return [{
          id: myCompany.id,
          name: myCompany.name
        }];
      }
      return [];
    } else {
      return companies.filter(company => company.status === 'ACTIVE');
    }
  };

  const filteredCompanies = getFilteredCompanies();

  return (
    <div className={width}>
      <CompanySelectWithLabel
        label="Компания"
        value={value != null ? value : 'null'}
        onChange={(val) => onChange(val && val !== 'null' ? Number(val) : undefined)}
        companies={filteredCompanies}
        includeNone={isSuperAdmin}
        openUpward={true}
        disabled={disabled || isAdminOrOwner}
      />
    </div>
  );
};

interface UserEditFormProps {
  userId?: number; // Если не указан, редактируется текущий пользователь
  onBack?: () => void;
  onSuccess?: () => void;
  mode?: 'personal' | 'admin'; // Режим: личная информация или админское редактирование
}

/**
 * Универсальный компонент формы редактирования пользователя
 * Поддерживает два режима:
 * - personal: редактирование собственной информации (текущий пользователь)
 * - admin: редактирование другого пользователя администратором
 */
export function UserEditForm({ userId, onBack, onSuccess, mode = 'admin' }: UserEditFormProps) {
  const navigate = useNavigate();
  const { user: currentUser, role: currentUserRole } = useAuthStore();
  const colors = useBrandingColors();
  // Загружаем информацию о компании только в админском режиме
  const { company: myCompany } = useMyCompany(mode === 'admin');
  
  const [formData, setFormData] = useState<UserEditFormData>({
    email: '',
    firstName: '',
    lastName: '',
    role: ListAccountsRole.RECRUITER,
    password: '',
    confirmPassword: '',
    companyId: undefined,
    isActive: true
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  
  // Состояние для проверки наличия владельца в компании
  const [companyHasOwner, setCompanyHasOwner] = useState(false);
  const [checkingOwner, setCheckingOwner] = useState(false);
  
  // Состояние для модалки переноса вакансий
  const [showTransferPositionsModal, setShowTransferPositionsModal] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState<UpdateAccountRequest | null>(null);
  
  // Состояние ошибок валидации
  const [validationErrors, setValidationErrors] = useState<{
    password?: string;
    confirmPassword?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  }>({});

  // Определяем конфигурацию формы в зависимости от режима
  const getFormConfig = (): FormConfig => {
    if (mode === 'personal') {
      return {
        title: 'Личная информация',
        showRoleField: false,
        showPasswordFields: true,
        showLogoutButton: true,
        submitButtonText: 'Сохранить',
        cancelButtonText: 'Выйти из аккаунта',
        isPersonalInfo: true
      };
    } else {
      return {
        title: 'Редактирование пользователя',
        showRoleField: true,
        showPasswordFields: true,
        showLogoutButton: false,
        submitButtonText: 'Сохранить',
        cancelButtonText: 'Отмена',
        isPersonalInfo: false
      };
    }
  };

  const config = getFormConfig();

  // Проверяем права администратора для режима admin
  const isAdmin =
    String(currentUserRole).toUpperCase() === 'ADMIN' ||
    String(currentUserRole).toUpperCase() === 'OWNER' ||
    String(currentUserRole).toUpperCase() === 'SUPER_ADMIN';
    
  const isSuperAdmin = String(currentUserRole).toUpperCase() === 'SUPER_ADMIN';

  // Проверка наличия владельца в компании
  const checkCompanyOwner = React.useCallback(async (companyId: number | undefined | null, excludeUserId?: number) => {
    if (companyId == null || !isSuperAdmin) {
      setCompanyHasOwner(false);
      return;
    }
    
    setCheckingOwner(true);
    try {
      const response = await publicApiClient.account.listAccounts(
        undefined,
        ListAccountsRole.OWNER,
        'ALL',
        companyId,
        0,
        10
      );
      const owners = response.data?.content || [];
      const hasOtherOwner = excludeUserId 
        ? owners.some((owner: any) => owner.id !== excludeUserId)
        : owners.length > 0;
      setCompanyHasOwner(hasOtherOwner);
    } catch (err) {
      console.error('Ошибка при проверке владельца компании:', err);
      setCompanyHasOwner(false);
    } finally {
      setCheckingOwner(false);
    }
  }, [isSuperAdmin]);

  // Проверяем владельца при изменении компании
  React.useEffect(() => {
    if (mode === 'admin' && formData.companyId != null) {
      checkCompanyOwner(formData.companyId, userId);
    } else {
      setCompanyHasOwner(false);
    }
  }, [mode, formData.companyId, userId, checkCompanyOwner]);

  // Загрузка данных пользователя и компаний
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Загружаем компании для админского режима
        if (mode === 'admin') {
          try {
            const role = String(currentUserRole).toUpperCase();
            let companiesResponse;
            
            if (role === 'ADMIN' || role === 'OWNER') {
              // Админ загружает только свою компанию
              const userCompanyId = (currentUser as any)?.companyId;
              if (userCompanyId) {
                const companyResponse = await publicApiClient.companies.getCompany(userCompanyId);
                setCompanies(companyResponse.data ? [companyResponse.data] : []);
              } else {
                setCompanies([]);
              }
            } else {
              // Суперадмин и рекрутер загружают все компании
              companiesResponse = await loadCompaniesForSelect();
              setCompanies(companiesResponse as Company[]);
            }
          } catch (err) {
            console.warn('Failed to load companies:', err);
            setCompanies([]);
          }
        }

        if (mode === 'personal') {
          // Для личной информации используем данные из authStore
          if (currentUser) {
            console.log('Loading personal user data from authStore:', currentUser);
            const userData = currentUser as User;
            
            setUser(userData);
            setFormData({
              email: userData.email || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              role: userData.role || ListAccountsRole.RECRUITER,
              password: '', // Пароль всегда остается пустым при загрузке
              confirmPassword: '', // Подтверждение пароля всегда остается пустым при загрузке
              companyId: userData.companyId,
              isActive: Boolean(userData.isActive)
            });
          }
        } else {
          // Для админского режима
          if (userId) {
            // Редактирование существующего пользователя
            console.log('Loading user data for userId:', userId);
            const response = await publicApiClient.account.getAccountById(userId);
            const userData = response.data as any;
            
            console.log('Loaded user data:', userData);
            setUser(userData);

            setFormData({
              email: userData.email || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              role: userData.role || ListAccountsRole.RECRUITER,
              password: '', // Пароль всегда остается пустым при загрузке
              confirmPassword: '', // Подтверждение пароля всегда остается пустым при загрузке
              companyId: userData.companyId,
              isActive: Boolean(userData.isActive)
            });
          } else {
            // Создание нового пользователя - автоматически устанавливаем компанию админа
            const role = String(currentUserRole).toUpperCase();
            const adminCompanyId = (currentUser as any)?.companyId;
            
            console.log('Creating new user, admin role:', role, 'admin companyId:', adminCompanyId);
            
            let finalCompanyId = undefined;
            
            if (isLimitedToOwnCompany(role as any)) {
              // Если companyId есть в пользователе, используем его
              if (adminCompanyId) {
                finalCompanyId = adminCompanyId;
                console.log('🔍 UserEditForm - Using companyId from user profile:', adminCompanyId);
              } else {
                console.log('🔍 UserEditForm - No companyId in user profile, but this should not happen for', role, 'role');
                // Для админа и рекрутера companyId должен быть в профиле, если его нет - это ошибка конфигурации
              }
            }
            
            setFormData({
              email: '',
              firstName: '',
              lastName: '',
              role: ListAccountsRole.RECRUITER, // По умолчанию создаем рекрутера
              password: '',
              confirmPassword: '',
              companyId: finalCompanyId, // Автоматически устанавливаем компанию админа
              isActive: true
            });
          }
        }
      } catch (err) {
        console.error('Error loading user data:', err);
        toast.error('Ошибка при загрузке данных пользователя');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userId, mode, currentUser, currentUserRole]);

  // Функция для валидации пароля
  const validatePassword = (password: string, confirmPassword: string) => {
    const errors: { password?: string; confirmPassword?: string } = {};
    
    // Валидируем пароль только если он заполнен
    if (password && password.length < 8) {
      errors.password = 'Пароль должен содержать минимум 8 символов';
    }
    
    // Валидируем подтверждение пароля только если пароль заполнен
    if (password && confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = 'Пароли не совпадают';
    }
    
    return errors;
  };

  // Функция для валидации email
  const validateEmail = (email: string) => {
    if (!email.trim()) {
      return 'Email обязателен для заполнения';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Введите корректный email адрес';
    }
    
    return null;
  };

  const handleFieldChange = (field: keyof UserEditFormData) => (value: string | ListAccountsRole | number) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Если очищается поле пароля, очищаем и поле подтверждения пароля
      if (field === 'password' && !value) {
        newData.confirmPassword = '';
        // Очищаем ошибки валидации пароля
        setValidationErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors.password;
          delete newErrors.confirmPassword;
          return newErrors;
        });
      }
      
      // Очищаем ошибки для изменяемого поля
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[field as keyof typeof newErrors];
        return newErrors;
      });
      
      // Валидация в реальном времени для паролей
      if (field === 'password' || field === 'confirmPassword') {
        const passwordErrors = validatePassword(
          field === 'password' ? value as string : newData.password,
          field === 'confirmPassword' ? value as string : newData.confirmPassword
        );
        
        if (Object.keys(passwordErrors).length > 0) {
          setValidationErrors(prevErrors => ({
            ...prevErrors,
            ...passwordErrors
          }));
        }
      }
      
      // Валидация email в реальном времени
      if (field === 'email') {
        const emailError = validateEmail(value as string);
        if (emailError) {
          setValidationErrors(prevErrors => ({
            ...prevErrors,
            email: emailError
          }));
        }
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔍 UserEditForm: handleSubmit called');
    console.log('🔍 UserEditForm: mode:', mode);
    console.log('🔍 UserEditForm: userId:', userId);
    console.log('🔍 UserEditForm: isAdmin:', isAdmin);
    console.log('🔍 UserEditForm: formData:', formData);
    
    // Валидация полей
    console.log('🔍 UserEditForm: Starting validation');
    
    // Очищаем предыдущие ошибки
    setValidationErrors({});
    
    const errors: typeof validationErrors = {};
    
    // Валидация обязательных полей
    if (!formData.firstName.trim()) {
      errors.firstName = 'Имя обязательно для заполнения';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Фамилия обязательна для заполнения';
    }
    
    // Валидация email
    const emailError = validateEmail(formData.email);
    if (emailError) {
      errors.email = emailError;
    }
    
    // Валидация пароля (только если он заполнен)
    if (formData.password) {
      const passwordErrors = validatePassword(formData.password, formData.confirmPassword);
      Object.assign(errors, passwordErrors);
    }
    
    // Если есть ошибки, показываем их и не отправляем форму
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      console.log('🔍 UserEditForm: Validation failed:', errors);
      return;
    }
    
    console.log('🔍 UserEditForm: Validation passed');
    
    try {
      setSaving(true);
      console.log('🔍 UserEditForm: Starting save process');
      console.log('🔍 UserEditForm: Saving user data:', formData);
      
      // Подготавливаем данные для отправки
      const updateData: UpdateAccountRequest = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email
      };
      
      const isEditingSelf = userId === currentUser?.id;
      const roleChangeAllowed = canChangeRole(
        String(currentUserRole || '').toUpperCase(),
        user?.role,
        isEditingSelf
      );
      
      if (mode === 'admin' && config.showRoleField && isAdmin && roleChangeAllowed) {
        updateData.role = formData.role;
      }
      
      // Добавляем companyId только в админском режиме
      if (mode === 'admin') {
        updateData.companyId = formData.companyId;
      }

      if (mode === 'admin' && isAdmin) {
        updateData.isActive = formData.isActive;
      }
      
      // Добавляем пароль только если он указан
      if (formData.password && formData.password.trim()) {
        updateData.password = formData.password;
        console.log('Password will be updated');
      }
      
      // Проверяем, изменилась ли компания (только для SUPER_ADMIN в админском режиме)
      const isSuperAdmin = String(currentUserRole).toUpperCase() === 'SUPER_ADMIN';
      const companyChanged = mode === 'admin' && isSuperAdmin && userId && user && 
        user.companyId !== undefined && 
        formData.companyId !== undefined && 
        user.companyId !== formData.companyId;
      
      if (companyChanged) {
        // Показываем модалку с вопросом о переносе вакансий
        setPendingUpdateData(updateData);
        setShowTransferPositionsModal(true);
        setSaving(false);
        return;
      }
      
      console.log('Sending update data:', updateData);
      
      await performUpdate(updateData);
      
    } catch (err: any) {
      console.error('Error saving user data:', err);
      
      // Показываем уведомление об ошибке
      const errorMessage = err.response?.data?.message || err.message || 'Произошла ошибка при сохранении';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const performUpdate = async (updateData: UpdateAccountRequest) => {
    try {
      setSaving(true);
      
      let updatedUser;
      
      if (mode === 'personal') {
        // Для личной информации используем apiService
        updatedUser = await apiService.updateAccount(updateData);
        
        // Обновляем кеш аватара если данные изменились
        if (currentUser) {
          const oldEmail = currentUser.email;
          const emailChanged = oldEmail !== formData.email;
          const nameChanged = currentUser.firstName !== formData.firstName || currentUser.lastName !== formData.lastName;
          
          if (emailChanged) {
            console.log('Email changed, updating avatar color cache');
            avatarColorCache.updateEmail(oldEmail || '', formData.email);
          }
          
          if (emailChanged || nameChanged) {
            console.log('User data changed, forcing avatar refresh');
          }
          
          // Обновляем данные в authStore
          useAuthStore.getState().setUser({
            ...updatedUser,
            role: updatedUser.role as ListAccountsRole
          });
          console.log('User data updated in authStore, components will re-render');
        }
      } else {
        // Для админского режима используем publicApiClient
        if (!userId) {
          throw new Error('User ID is required for admin mode');
        }
        updatedUser = await publicApiClient.account.updateAccountById(userId, updateData);
      }
      
      console.log('User data saved successfully:', updatedUser);
      
      // Показываем уведомление об успехе
      const successMessage = mode === 'personal' 
        ? 'Личная информация успешно обновлена!' 
        : 'Данные пользователя успешно обновлены!';
      toast.success(successMessage);
      
      // Вызываем callback успеха
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error saving user data:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleTransferPositionsChoice = async (transferType: 'NONE' | 'CURRENT_COMPANY' | 'ALL') => {
    if (!pendingUpdateData) return;
    
    setShowTransferPositionsModal(false);
    
    const updateDataWithTransfer = {
      ...pendingUpdateData,
      transferPositions: transferType
    };
    
    setPendingUpdateData(null);
    
    try {
      await performUpdate(updateDataWithTransfer);
    } catch (err: any) {
      console.error('Error saving user data:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Произошла ошибка при сохранении';
      toast.error(errorMessage);
    }
  };

  const handleLogout = () => {
    useAuthStore.getState().logout();
    navigate('/recruiter/login');
  };

  // Показываем загрузку пока данные не загружены
  if (loading) {
    return (
      <div className={`bg-[#f5f6f1] box-border content-stretch flex flex-col gap-10 items-start justify-start relative shrink-0 w-full h-full ${
        mode === 'personal' ? 'p-[24px] rounded-[24px]' : ''
      }`}>
        <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[24px] text-left text-nowrap">
          <p className="block leading-[30px] whitespace-pre">{config.title}</p>
        </div>
        <div className="flex items-center justify-center w-full">
          <div className="text-[#525866] text-[16px]">Загрузка данных...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[#f5f6f1] box-border content-stretch flex flex-col gap-6 items-start justify-start relative shrink-0 w-full h-full ${
      mode === 'personal' ? 'p-[24px] rounded-[24px]' : ''
    }`}>
      {/* Header with Back Button */}
      <div className="box-border content-stretch flex flex-row items-center justify-between p-0 relative shrink-0 w-full">
        <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[24px] text-left text-nowrap">
          <p className="block leading-[30px] whitespace-pre">{config.title}</p>
        </div>
        
        {onBack && (
          <button
            onClick={onBack}
            className="transition-colors text-[15px] font-medium"
            style={{ color: colors.primary }}
            onMouseEnter={(e) => (e.currentTarget.style.color = colors.primaryHover)}
            onMouseLeave={(e) => (e.currentTarget.style.color = colors.primary)}
          >
            ← Назад к списку
          </button>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="box-border content-stretch flex flex-col gap-6 items-start justify-start p-0 relative shrink-0 w-full">
        {/* Скрытые поля-ловушки для предотвращения автозаполнения */}
        <>
          <input 
            type="text" 
            style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} 
            tabIndex={-1} 
            autoComplete="username" 
            name="fake-username"
          />
          <input 
            type="password" 
            style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} 
            tabIndex={-1} 
            autoComplete="current-password" 
            name="fake-password"
          />
          <input 
            type="email" 
            style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} 
            tabIndex={-1} 
            autoComplete="email" 
            name="fake-email"
          />
        </>
        {/* Form Fields */}
        <div className="box-border content-stretch flex flex-col gap-3 items-start justify-start p-0 relative shrink-0 w-full">
          <FigmaTextInput
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleFieldChange('email')}
            width="w-[555px]"
            required
            error={validationErrors.email}
            disableAutofill={mode === 'personal'}
          />

          {config.showPasswordFields && (
            <>
              <FigmaTextInput
                label="Пароль"
                type="password"
                value={formData.password}
                onChange={handleFieldChange('password')}
                width="w-[555px]"
                error={validationErrors.password}
                placeholder="Оставьте пустым, если не хотите менять пароль"
                disableAutofill={true}
              />

              {/* Поле подтверждения пароля показывается только если введен пароль */}
              {formData.password && (
                <FigmaTextInput
                  label="Подтверждение пароля"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleFieldChange('confirmPassword')}
                  width="w-[555px]"
                  error={validationErrors.confirmPassword}
                  placeholder="Повторите пароль"
                  disableAutofill={true}
                />
              )}
            </>
          )}

          <FigmaTextInput
            label="Имя"
            value={formData.firstName}
            onChange={handleFieldChange('firstName')}
            width="w-[555px]"
            required
            error={validationErrors.firstName}
          />

          <FigmaTextInput
            label="Фамилия"
            value={formData.lastName}
            onChange={handleFieldChange('lastName')}
            width="w-[555px]"
            required
            error={validationErrors.lastName}
          />

          {/* Поле роли доступно только в админском режиме и для администраторов */}
          {config.showRoleField && isAdmin && (
            <RoleSelectComponent
              value={formData.role}
              onChange={handleFieldChange('role')}
              width="w-[555px]"
              targetUserRole={user?.role}
              isEditingSelf={userId === currentUser?.id}
              hasCompany={formData.companyId != null}
              companyHasOwner={companyHasOwner}
            />
          )}

          {/* Поле выбора компании доступно только в админском режиме */}
          {mode === 'admin' && (
            <CompanySelect
              value={formData.companyId}
              onChange={(value) => {
                setFormData(prev => {
                  const newData = { ...prev, companyId: value };
                  const companyChanged = prev.companyId !== value;
                  if (prev.role === ListAccountsRole.OWNER && companyChanged) {
                    newData.role = ListAccountsRole.RECRUITER;
                  }
                  return newData;
                });
              }}
              companies={companies}
              width="w-[555px]"
              currentUserRole={String(currentUserRole || '')}
              myCompany={myCompany}
              currentUserCompanyId={(currentUser as any)?.companyId}
            />
          )}

          {mode === 'admin' && isAdmin && (
            <div className="w-[555px]">
              <SelectWithLabel
                label="Статус"
                value={formData.isActive ? 'true' : 'false'}
                onChange={(val) => setFormData(prev => ({ ...prev, isActive: String(val) === 'true' }))}
                options={[
                  { value: 'true', label: 'Активный' },
                  { value: 'false', label: 'Неактивный' }
                ]}
                openUpward={true}
                disabled={(() => {
                  const currentRole = String(currentUserRole || '').toUpperCase();
                  const targetRole = String(user?.role || '').toUpperCase();
                  const isEditingSelf = Boolean(userId && currentUser?.id && userId === currentUser.id);
                  if (isEditingSelf) return false;
                  if (targetRole === 'ADMIN' && currentRole === 'ADMIN') return true;
                  if (targetRole === 'OWNER' && currentRole !== 'SUPER_ADMIN') return true;
                  if (targetRole === 'SUPER_ADMIN') return currentRole !== 'SUPER_ADMIN';
                  return false;
                })()}
              />
            </div>
          )}
        </div>

        {/* Submit and Action Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 text-white p-[10px] h-[44px] rounded-2xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed css-rpndqk font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[16px] text-center tracking-[-0.176px]"
            style={{ backgroundColor: colors.primary }}
            onMouseEnter={(e) => { if (!saving) e.currentTarget.style.backgroundColor = colors.primaryHover; }}
            onMouseLeave={(e) => { if (!saving) e.currentTarget.style.backgroundColor = colors.primary; }}
          >
            {saving ? 'Сохранение...' : config.submitButtonText}
          </button>

          {config.showLogoutButton ? (
            <button
              type="button"
              onClick={handleLogout}
              className="bg-[#868c98] relative rounded-2xl shrink-0 inline-flex hover:bg-[#6b717d] transition-colors px-[24px] py-[10px]"
            >
              <div className="css-rpndqk font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#ffffff] text-[16px] text-center text-nowrap tracking-[-0.176px]">
                <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">
                  {config.cancelButtonText}
                </p>
              </div>
            </button>
          ) : (
            onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex-1 border border-[#e2e4e9] text-[#525866] p-[10px] h-[44px] rounded-2xl hover:bg-[#f8f9fa] transition-colors css-2x1u1o font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[16px] text-center tracking-[-0.176px]"
              >
                {config.cancelButtonText}
              </button>
            )
          )}
        </div>
      </form>

      {/* Модалка для выбора переноса вакансий */}
      {showTransferPositionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4" role="dialog" aria-modal="true">
            <div className="p-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                  </svg>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                Перенос вакансий
              </h3>
              
              <p className="text-gray-600 mb-2 text-center leading-relaxed">
                Компания пользователя изменена. Что делать с вакансиями, которые он создал?
              </p>
              
              <p className="text-gray-600 text-sm mb-4 text-center">
                Выберите, какие вакансии перенести в новую компанию.
              </p>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleTransferPositionsChoice('NONE')}
                  className="w-full px-[24px] py-[14px] h-[52px] rounded-2xl border border-[#e2e4e9] text-[#111827] hover:bg-[#f8f9fa] transition-colors font-['Inter:Medium',_sans-serif] font-medium text-[16px] text-center"
                >
                  Оставить все в старой компании
                </button>
                <button
                  onClick={() => handleTransferPositionsChoice('CURRENT_COMPANY')}
                  className="w-full px-[24px] py-[14px] h-[52px] rounded-2xl border border-[#e2e4e9] text-[#111827] hover:bg-[#f8f9fa] transition-colors font-['Inter:Medium',_sans-serif] font-medium text-[16px] text-center"
                >
                  Перенести только из текущей компании
                </button>
                <button
                  onClick={() => handleTransferPositionsChoice('ALL')}
                  className="w-full px-[24px] py-[14px] h-[52px] rounded-2xl text-white transition-colors font-['Inter:Medium',_sans-serif] font-medium text-[16px] text-center"
                  style={{ 
                    backgroundColor: colors.primary,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primaryHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.primary;
                  }}
                >
                  Перенести все вакансии
                </button>
                <button
                  onClick={() => {
                    setShowTransferPositionsModal(false);
                    setPendingUpdateData(null);
                  }}
                  className="w-full px-[24px] py-[14px] h-[52px] rounded-2xl border border-[#e2e4e9] text-[#525866] hover:bg-[#f8f9fa] transition-colors font-['Inter:Medium',_sans-serif] font-medium text-[16px] text-center"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
